import { useEffect, useMemo, useState } from 'react'

import { SelectOptionGroups } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { Salary, SalaryPeriodicity, Workplace } from 'modules/job_catalog/lib/salary'
import { defaultCurrency, defaultPeriodicity } from 'modules/job_catalog/lib/salaryUtils'

import { SalaryErrors, ValidationContext, validator } from './validations'

export const useSalary = (initialSalary?: Salary, validationContext?: ValidationContext) => {
  const salaryId = useMemo(() => initialSalary?.id, [initialSalary])
  const [minAmount, setMinAmount] = useState<string>(String(initialSalary?.min))
  const [maxAmount, setMaxAmount] = useState<string>(String(initialSalary?.max))
  const [grossSalary, setGrossSalary] = useState<string>(String(initialSalary?.gross))

  const [currency, setCurrency] = useState<string>(initialSalary?.currency ?? defaultCurrency)
  const [workplaces, setWorkplaces] = useState<Workplace[]>(initialSalary?.workplaces ?? [])
  const [periodicity, setPeriodicity] = useState<string>(
    initialSalary?.periodicity ?? defaultPeriodicity
  )
  const salaryPeriodicityOptions: SelectOptionGroups = [
    {
      label: i18n.t('job_catalog.nodes.salary_modal.periodicities.yearly'),
      value: SalaryPeriodicity.yearly,
    },
    {
      label: i18n.t('job_catalog.nodes.salary_modal.periodicities.monthly'),
      value: SalaryPeriodicity.monthly,
    },
    {
      label: i18n.t('job_catalog.nodes.salary_modal.periodicities.weekly'),
      value: SalaryPeriodicity.weekly,
    },
    {
      label: i18n.t('job_catalog.nodes.salary_modal.periodicities.daily'),
      value: SalaryPeriodicity.daily,
    },
    {
      label: i18n.t('job_catalog.nodes.salary_modal.periodicities.hourly'),
      value: SalaryPeriodicity.hourly,
    },
  ]

  const initialRange = useMemo(
    () => (grossSalary && grossSalary !== 'undefined' ? 'fixed' : 'range'),
    [grossSalary]
  )
  const [type, setType] = useState<string>(initialRange)
  const salaryTypeOptions: SelectOptionGroups = [
    {
      label: i18n.t('job_catalog.nodes.salary_modal.types.range'),
      value: 'range',
    },
    {
      label: i18n.t('job_catalog.nodes.salary_modal.types.fixed'),
      value: 'fixed',
    },
  ]

  const isTypeRange = useMemo(() => type === 'range', [type])

  const [errors, setErrors] = useState<SalaryErrors>({})

  const validate = useMemo(
    () => validator(setErrors, validationContext),
    [setErrors, validationContext]
  )

  useEffect(() => {
    let salary: Salary
    if (isTypeRange) {
      salary = {
        min: Number(minAmount),
        max: Number(maxAmount),
        currency,
        periodicity,
        workplaces,
      }
    } else {
      salary = {
        gross: Number(grossSalary),
        currency,
        periodicity,
        workplaces,
      }
    }
    validate(salary)
  }, [minAmount, maxAmount, grossSalary, validate, isTypeRange, currency, periodicity, workplaces])

  const isValid = useMemo(() => {
    if (Object.keys(errors).length !== 0) return false
    if (isTypeRange) return hasValue(minAmount) && hasValue(maxAmount)
    return hasValue(grossSalary)
  }, [minAmount, maxAmount, grossSalary, isTypeRange, errors])

  const buildSalary = (): Salary => {
    if (type === 'fixed') {
      return {
        id: salaryId,
        currency,
        periodicity,
        workplaces,
        gross: Number(grossSalary),
      }
    } else {
      return {
        id: salaryId,
        currency,
        periodicity,
        workplaces,
        max: Number(maxAmount),
        min: Number(minAmount),
      }
    }
  }

  return {
    minAmount,
    maxAmount,
    grossSalary,
    currency,
    workplaces,
    periodicity,
    salaryPeriodicityOptions,
    type,
    salaryTypeOptions,
    isTypeRange,
    isValid,
    errors,
    setMinAmount,
    setMaxAmount,
    setGrossSalary,
    setCurrency,
    setWorkplaces,
    setPeriodicity,
    setType,
    buildSalary,
  }
}

function hasValue(amount: string): boolean {
  return !!amount && amount !== 'undefined'
}
